import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Filter, Plus, Search, ChevronDown, ChevronUp, Trophy, Check } from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import { Badge, Button, Card, CardContent, Input, Label, Select, SelectTrigger, SelectContent, SelectItem, SelectValue, Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui';
import { useAuth } from '../../hooks/useAuth';
import { useOrganization } from '../../contexts/OrganizationContext';
import { puedeEditarJuego } from '../../lib/torneoOrigen';
import { puedeCapturarJuegos } from '../../lib/permisosJuegos';
import { torneosParaCrearJuego } from '../../lib/juegosCreables';
import { filtrarPorMundo, MUNDO_INTERNO } from '../../lib/mundoTorneo';
import { usePermissions } from '../../hooks/usePermissions';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { getAllGroups, getJuegosByGrupoIds, getStaffGroups, deleteJuego, createJuego, getGruposAsignadosByTorneo, getTorneosDelClub } from '../../lib/supabase';
import JuegoCard from './JuegoCard';
import FormJuego from './FormJuego';
import { fixMojibake } from '../../lib/utils';

const MisJuegos = () => {
  const { user, staff } = useAuth();
  // Por id, no por objeto: un usuario nuevo de la misma persona (al volver del segundo
  // plano) no debe recargar. Misma leccion que OrganizationContext.
  const userId = user?.id;
  const { org } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isHeadCoach, role, isPlatformAdmin, canTakeAttendance } = usePermissions();
  const { confirm, alert, ConfirmDialog } = useConfirmDialog();

  const [groups, setGroups] = useState([]);
  const [staffGroupIds, setStaffGroupIds] = useState([]);
  const [staffGroups, setStaffGroups] = useState([]);
  const [juegos, setJuegos] = useState([]);
  const [torneosClub, setTorneosClub] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewTab, setViewTab] = useState('mios');
  const [filterGrupo, setFilterGrupo] = useState('all');
  const [filterTorneo, setFilterTorneo] = useState(location.state?.torneoId || 'all');
  const [filterEstatus, setFilterEstatus] = useState('all');

  // Create form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createTorneoId, setCreateTorneoId] = useState('');
  const [createGrupoId, setCreateGrupoId] = useState(null);
  const [createGrupos, setCreateGrupos] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      let groupData = [];
      let myGroupIds = [];
      let myStaffGroups = [];
      if (isAdmin || isHeadCoach) {
        const { data, error } = await getAllGroups();
        if (error) throw error;
        groupData = (data || []).filter(Boolean);
        if (staff?.id) {
          const { data: staffData } = await getStaffGroups(staff.id);
          myStaffGroups = staffData || [];
          myGroupIds = myStaffGroups.map((item) => item.grupos?.id).filter(Boolean);
        }
      } else if (staff?.id) {
        const { data, error } = await getStaffGroups(staff.id);
        if (error) throw error;
        myStaffGroups = data || [];
        groupData = myStaffGroups.map((item) => item.grupos).filter(Boolean);
        myGroupIds = groupData.map((g) => g.id);
      }
      setGroups(groupData);
      setStaffGroupIds(myGroupIds);
      setStaffGroups(myStaffGroups);

      // Los torneos del club, no los que se adivinan de los juegos: un torneo recien
      // creado no tiene ninguno todavia y ahi es justo donde se captura el primero.
      // Ligeros y PRIMERO: el desplegable de "Crear juego" se llena de aqui, y antes
      // esperaba a la consulta pesada de inscripciones y a una consulta de juegos por
      // cada grupo (14 en un club real).
      const { data: torneosData } = await getTorneosDelClub();
      setTorneosClub(torneosData || []);

      const merged = (await getJuegosByGrupoIds(groupData.map((group) => group.id))).filter(Boolean);
      const uniqueMap = new Map();
      merged.forEach((juego) => { uniqueMap.set(juego.id, juego); });
      setJuegos(Array.from(uniqueMap.values()));
    } catch (error) {
      console.error('Error cargando juegos:', error);
      setGroups([]);
      setJuegos([]);
      setTorneosClub([]);
    } finally {
      setLoading(false);
    }
  }, [userId, staff?.id, isAdmin, isHeadCoach]);

  useEffect(() => { loadData(); }, [loadData]);

  // Torneos derivados de los juegos — solo activos/en_curso
  const torneosDisponibles = useMemo(() => {
    const map = new Map();
    juegos.forEach((juego) => {
      if (juego.torneo?.id) {
        map.set(juego.torneo.id, juego.torneo);
      }
    });
    return Array.from(map.values()).filter((t) => {
      const est = (t.estatus || '').toLowerCase();
      return est !== 'cancelado'; // Mostrar planeado, en_curso, finalizado — no cancelados
    });
  }, [juegos]);

  // El desplegable de crear y la busqueda del torneo elegido leen de aqui, las dos.
  // Cuando eran dos listas distintas, elegir un torneo de la red daba undefined.
  const torneosCreables = useMemo(
    () => torneosParaCrearJuego(torneosClub, org?.id),
    [torneosClub, org?.id]
  );

  // La decision es POR GRUPO y vive en un solo lugar (lib/permisosJuegos), que espeja
  // la politica de la base. Antes se decidia aqui y solo por rol: la pantalla le
  // ofrecia a un coach editar y borrar juegos de cualquier grupo, y la base lo
  // rechazaba al intentarlo.
  const puedeEnGrupo = useCallback(
    (grupoId) => puedeCapturarJuegos({
      rol: role,
      esPlatformAdmin: isPlatformAdmin,
      asignaciones: staffGroups,
      grupoId,
    }),
    [role, isPlatformAdmin, staffGroups]
  );

  const puedeCrearAlgunJuego = useMemo(
    () => staffGroups.some((g) => puedeEnGrupo(g.grupo_id || g.id)) || role === 'admin' || isPlatformAdmin,
    [staffGroups, puedeEnGrupo, role, isPlatformAdmin]
  );

  const createGruposPermitidos = useMemo(
    () => createGrupos.filter((g) => puedeEnGrupo(g.id)),
    [createGrupos, puedeEnGrupo]
  );

  // Grupos que tienen juegos
  const gruposConJuegos = useMemo(() => {
    const map = new Map();
    juegos.forEach((juego) => {
      const g = juego.grupos || juego.grupo;
      if (g?.id) map.set(g.id, g);
    });
    return Array.from(map.values());
  }, [juegos]);

  const juegosAntesDeMundo = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return juegos.filter((juego) => {
      if (viewTab === 'mios' && staffGroupIds.length > 0 && !staffGroupIds.includes(juego.grupo_id)) return false;
      if (filterGrupo !== 'all' && juego.grupo_id !== filterGrupo) return false;
      if (filterTorneo !== 'all' && juego.torneo?.id !== filterTorneo) return false;
      if (filterEstatus !== 'all' && juego.estatus !== filterEstatus) return false;
      if (term) {
        const matchSearch =
          juego.nombre?.toLowerCase().includes(term) ||
          juego.equipo_rival?.toLowerCase().includes(term) ||
          juego.torneo?.nombre?.toLowerCase().includes(term) ||
          juego.grupos?.nombre_del_grupo?.toLowerCase().includes(term);
        if (!matchSearch) return false;
      }
      return true;
    });
  }, [juegos, filterGrupo, filterTorneo, filterEstatus, searchTerm, viewTab, staffGroupIds]);

  // SOLO torneos internos (el dueno, 2026-09-07: "volvimos a mezclar los juegos"). Esta
  // pantalla es de Torneos internos: aqui se crea, se edita y se pone marcador. Los de
  // la red viven en MYKLUBB Torneos > Juegos; los del dia, en Hoy; el mes
  // completo de los dos mundos, en Agendas > Calendario de juegos.
  const juegosFiltrados = useMemo(
    () => filtrarPorMundo(juegosAntesDeMundo, MUNDO_INTERNO, org?.id),
    [juegosAntesDeMundo, org?.id]
  );

  // Agrupados por torneo. Habia un selector "Agrupar por" (torneo | grupo): una perilla
  // mas en una pantalla que ya tenia diez, y "por grupo" lo resuelve el filtro Grupo.
  const juegosAgrupados = useMemo(() => {
    const map = new Map();
    juegosFiltrados.forEach((juego) => {
      const key = juego.torneo?.id || 'sin-torneo';
      const label = fixMojibake(juego.torneo?.nombre || 'Sin torneo');
      if (!map.has(key)) map.set(key, { label, juegos: [] });
      map.get(key).juegos.push(juego);
    });
    return Array.from(map.values());
  }, [juegosFiltrados]);

  // Unica entrada generica al modal, y por eso el lugar donde se limpia el grupo fijado
  // por "Nuevo juego"/"Nuevo partido" (createGrupoId): sin esto, abrir el modal desde la
  // barra despues de haber llegado con un grupo puesto hereda un lockedGroup que nadie
  // pidio. La otra entrada (location.state) fija el suyo explicitamente.
  const handleOpenCreate = () => {
    setCreateTorneoId('');
    setCreateGrupos([]);
    setCreateGrupoId(null);
    setShowCreateForm(true);
  };

  const cerrarCreateForm = () => {
    setShowCreateForm(false);
  };

  const handleTorneoChangeForCreate = async (torneoId) => {
    setCreateTorneoId(torneoId);
    if (torneoId) {
      const { data } = await getGruposAsignadosByTorneo(torneoId);
      setCreateGrupos(data || []);
    } else {
      setCreateGrupos([]);
    }
  };

  // Los botones "Nuevo juego" (tarjeta del grupo) y "Nuevo partido" (roster) llegan
  // aqui con el torneo puesto. Antes navegaban a /torneos/juegos/gestion, que solo
  // redirige desde 7456b3e: el usuario caia en la lista sin formulario y sin torneo.
  useEffect(() => {
    if (!location.state?.abrirNuevoJuego) return;
    const { torneoId, grupoId } = location.state;
    setShowCreateForm(true);
    setCreateGrupoId(grupoId || null);
    if (torneoId) handleTorneoChangeForCreate(torneoId);
    // Se limpia el state para que recargar o volver atras no reabra el modal.
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitCreate = async (juegoData) => {
    try {
      setSaving(true);
      const { error } = await createJuego({ ...juegoData, torneo_id: createTorneoId });
      if (error) throw error;
      cerrarCreateForm();
      loadData();
    } catch (err) {
      await alert('Error', err.message || 'No se pudo crear el juego.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteJuego = async (juego) => {
    const rival = juego.equipo_rival || juego.equipos?.nombre || 'rival';
    if (!await confirm('Eliminar juego', `¿Eliminar el juego vs ${rival}?`, { destructive: true })) return;
    // deleteJuego LANZA si falla (como createJuego y getJuegoById); no devuelve
    // { error }. Sin este try el fallo se perdia en una promesa rechazada.
    try {
      await deleteJuego(juego.id);
    } catch (err) {
      await alert('Error', err.message || 'No se pudo eliminar el juego.');
      return;
    }
    loadData();
  };

  const filterCount = [filterGrupo !== 'all', filterTorneo !== 'all', filterEstatus !== 'all'].filter(Boolean).length;

  return (
    <FeaturedLayout
      title="Juegos"
      subtitle="Los juegos de los torneos que organiza tu club. Los de la red están en MYKLUBB Torneos > Juegos."
      refreshAction={loadData}
    >
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-nowrap">
          <div className="flex-1 min-w-0 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input placeholder="Buscar por rival, torneo o grupo..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
          <span className="text-sm text-muted-foreground hidden sm:block">
            <span className="font-medium text-foreground">{juegosFiltrados.length}</span> juegos
          </span>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="relative h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3">
            <Filter className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Filtros</span>
            {showFilters ? <ChevronUp className="h-4 w-4 ml-2 hidden sm:inline" /> : <ChevronDown className="h-4 w-4 ml-2 hidden sm:inline" />}
          </Button>
          {puedeCrearAlgunJuego && (
            <Button onClick={handleOpenCreate} className="h-10" aria-label="Crear juego" title="Crear juego">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Crear juego</span>
            </Button>
          )}
        </div>

        {/* Alcance: mismo lenguaje que los chips de /torneos, en vez de un segmented aparte */}
        {(isAdmin || isHeadCoach) && (
          <div className="flex flex-wrap gap-1.5">
            <Button
              variant={viewTab === 'mios' ? 'default' : 'outline'}
              size="sm"
              className="h-8 rounded-full px-3 text-xs font-normal"
              onClick={() => setViewTab(viewTab === 'mios' ? 'todos' : 'mios')}
            >
              {viewTab === 'mios' && <Check className="h-3 w-3 mr-1.5" />}
              Solo mis juegos
            </Button>
          </div>
        )}

        {showFilters && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Torneo</Label>
                <Select value={filterTorneo} onValueChange={setFilterTorneo}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los torneos</SelectItem>
                    {torneosDisponibles.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{fixMojibake(t.nombre || 'Sin nombre')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Grupo</Label>
                <Select value={filterGrupo} onValueChange={setFilterGrupo}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los grupos</SelectItem>
                    {(viewTab === 'mios' ? groups.filter((g) => staffGroupIds.includes(g.id)) : groups).map((g) => (
                      <SelectItem key={g.id} value={g.id}>{fixMojibake(g.nombre_del_grupo)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estatus</Label>
                <Select value={filterEstatus} onValueChange={setFilterEstatus}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="programado">Programado</SelectItem>
                    <SelectItem value="en_curso">En curso</SelectItem>
                    <SelectItem value="terminado">Terminado</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                    <SelectItem value="pospuesto">Pospuesto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {filterCount > 0 && (
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={() => { setFilterGrupo('all'); setFilterTorneo('all'); setFilterEstatus('all'); }}>
                  Limpiar filtros
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Lista de juegos */}
        {loading ? (
          <p className="text-muted-foreground text-sm py-8 text-center">Cargando juegos...</p>
        ) : juegosFiltrados.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Trophy className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground mb-2">
                {juegos.length === 0 ? 'No hay juegos registrados en torneos activos' : 'No se encontraron juegos con los filtros aplicados'}
              </p>
              {juegos.length === 0 && puedeCrearAlgunJuego && (
                <Button size="sm" className="mt-3" onClick={handleOpenCreate}>
                  <Plus className="h-4 w-4 mr-1" /> Crear primer juego
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {juegosAgrupados.map(({ label, juegos: items }) => (
              <div key={label}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-base font-semibold">{label}</h3>
                  <Badge variant="secondary" className="text-xs">{items.length}</Badge>
                </div>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {items.map((juego) => (
                    <JuegoCard
                      key={juego.id}
                      juego={juego}
                      orgId={org?.id}
                      compact
                      onView={() => navigate(`/torneos/juegos/${juego.id}`)}
                      onEdit={puedeEnGrupo(juego.grupo_id) && puedeEditarJuego(juego, org?.id) ? () => navigate(`/torneos/juegos/${juego.id}`, { state: { focus: 'editar' } }) : null}
                      onDelete={puedeEnGrupo(juego.grupo_id) && puedeEditarJuego(juego, org?.id) ? () => handleDeleteJuego(juego) : null}
                      onTakeAttendance={canTakeAttendance ? () => {
                        const gId = juego.grupo_id || juego.grupos?.id;
                        if (gId) navigate(`/attendance/juego/${gId}/${juego.id}`);
                      } : null}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Formulario de crear juego */}
      {/* Era una capa fija a mano (no pasaba por el Dialog de la base: ni pantalla
          completa en telefono, ni Escape, ni foco). */}
      <Dialog open={showCreateForm} onOpenChange={(open) => { if (!open) cerrarCreateForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Crear Juego</DialogTitle>
          </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                {/* "Torneo interno" y no "Torneo": la lista YA venia filtrada -- solo
                    ofrece torneos donde el club captura --, pero no lo decia, y en una
                    pantalla que muestra los dos mundos eso se lee como mezcla. */}
                <Label>Torneo interno *</Label>
                <Select value={createTorneoId} onValueChange={handleTorneoChangeForCreate}>
                  <SelectTrigger><SelectValue placeholder="Selecciona un torneo" /></SelectTrigger>
                  <SelectContent>
                    {torneosCreables.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{fixMojibake(t.nombre || 'Sin nombre')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Solo los torneos que lleva tu club. En los de MYKLUBB Torneos el rol de
                  juegos lo programa la organizadora.
                </p>
              </div>
              {createTorneoId ? (
                <FormJuego
                  torneo={torneosCreables.find((t) => t.id === createTorneoId)}
                  gruposTorneo={createGruposPermitidos}
                  lockedGroup={createGrupoId ? createGruposPermitidos.find((g) => g.id === createGrupoId) || null : null}
                  onSubmit={handleSubmitCreate}
                  onCancel={cerrarCreateForm}
                  saving={saving}
                />
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  {torneosCreables.length === 0
                    ? (loading ? 'Cargando torneos...' : 'Solo puedes crear juegos en los torneos de tu club. Los partidos de un torneo de la red MYKLUBB los programa la organizadora.')
                    : 'Selecciona un torneo para continuar'}
                </p>
              )}
            </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </FeaturedLayout>
  );
};

export default MisJuegos;
